import React, { useState } from "react";

import { useGetIdentity, useTranslation } from "@refinedev/core";

import {
  Box,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  FormControlLabel,
  Link,
  Typography,
} from "@mui/material";

import { CardButton } from "@/components/common";
import { getRefreshUserInfo } from "@/providers/auth/fhir-auth-provider";
import type { IUser } from "@/types/types";
import { UPDATE_CONSENT_MUTATION } from "@/utils/GraphQLClient";
import type { ChangeUserConsentInput, UpdateConsentData } from "@/utils/GraphQLClient";

import { useMutation } from "urql";

const VITALL_TERMS = "https://www.vitall.com/legal#terms-and-conditions";
const VITALL_PRIVACY = "https://www.vitall.com/legal#privacy-notice";

interface ConsentModalProps {
  open: boolean;
  onAccepted: () => void;
}

export const ConsentModal: React.FC<ConsentModalProps> = ({ open, onAccepted }) => {
  const { translate } = useTranslation();
  const { data: user } = useGetIdentity<IUser>();

  const [accepted, setAccepted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [updateConsentResult, updateConsentMutation] = useMutation<
    UpdateConsentData,
    { input: ChangeUserConsentInput }
  >(UPDATE_CONSENT_MUTATION);
  const isSubmitting = updateConsentResult.fetching;

  const handleSubmit = async () => {
    if (!accepted || !user?.id) {
      return;
    }

    setError(null);

    try {
      const result = await updateConsentMutation({
        input: {
          user_id: String(user.id),
          toc_completed: true,
          pn_completed: true,
        },
      });

      if (result.error) {
        setError(result.error.graphQLErrors?.[0]?.message ?? result.error.message ?? translate("consent.errorMessage"));
        return;
      }

      if (result.data?.updateVitallConsent.success) {
        // Refresh user identity so the overlay detects consent is now accepted
        const refreshUserInfo = getRefreshUserInfo();
        await refreshUserInfo();
        onAccepted();
      } else {
        setError(translate("consent.errorMessage"));
      }
    } catch {
      setError(translate("consent.errorMessage"));
    }
  };

  return (
    <Dialog open={open} maxWidth="sm" fullWidth data-testid="consent-modal">
      <Typography variant="h1" mt={3} color="primary">
        {translate("consent.title")}
      </Typography>
      <DialogContent>
        <Typography variant="body1" color="text.secondary" mb={3} textAlign="center">
          {translate("consent.description")}
        </Typography>

        <FormControlLabel
          control={
            <Checkbox
              checked={accepted}
              onChange={e => setAccepted(e.target.checked)}
              disabled={isSubmitting}
              data-testid="consent-checkbox"
            />
          }
          label={
            <Typography variant="body2">
              {translate("consent.checkboxPrefix")}{" "}
              <Link href={VITALL_TERMS} target="_blank" rel="noopener noreferrer" underline="hover">
                {translate("consent.termsAndConditions")}
              </Link>{" "}
              {translate("consent.and")}{" "}
              <Link href={VITALL_PRIVACY} target="_blank" rel="noopener noreferrer" underline="hover">
                {translate("consent.privacyNotice")}
              </Link>
              .
            </Typography>
          }
        />

        {error && (
          <Typography color="error.dark" variant="body2" mt={2} data-testid="consent-error">
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions>
        <Box width="100%" px={2} pb={2}>
          <CardButton onClick={handleSubmit} disabled={!accepted || isSubmitting} data-testid="consent-submit-button">
            {isSubmitting ? <CircularProgress size={24} color="inherit" /> : translate("consent.submit")}
          </CardButton>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
